import { useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogFooter,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  calcLockDate,
  fmtDate,
  BOTTLE_TYPES,
  BOTTLE_FORMATS,
  type BottlingSku,
} from "@/lib/bottling-constants";

// ── Helpers ───────────────────────────────────────────────────────────────

function FormField({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="flex flex-col gap-1.5">
      <Label>{label}</Label>
      {children}
    </div>
  );
}

function SummaryRow({ label, value }: { label: string; value: string | number }) {
  return (
    <div className="flex justify-between border-b border-dashed border-border pb-1.5 last:border-0 last:pb-0 text-sm">
      <span className="text-muted-foreground">{label}</span>
      <strong>{value}</strong>
    </div>
  );
}

function CheckRow({ checked, onChange, label }: { checked: boolean; onChange: (v: boolean) => void; label: string }) {
  return (
    <label className="flex items-center gap-2 text-sm cursor-pointer">
      <input type="checkbox" checked={checked} onChange={e => onChange(e.target.checked)} className="accent-primary" />
      {label}
    </label>
  );
}

// ── SKU Modal ─────────────────────────────────────────────────────────────

const BLANK: BottlingSku = {
  brand: "", wine: "", vintage: "", bottle: "", format: "750ml", closure: "", label: "Yes",
  capsule: "No", casePack: 12, cases: 0, filter: "", dmdc: "No", fanciful: "", ava: "",
  lotCode: "", instructions: "",
};

export function SkuModal({ open, onClose, onAdd }: { open: boolean; onClose: () => void; onAdd: (sku: BottlingSku) => void }) {
  const [form, setForm] = useState<BottlingSku>(BLANK);
  const set = (f: keyof BottlingSku, v: any) => setForm(p => ({ ...p, [f]: v }));

  function handleAdd() {
    if (!form.brand || !form.wine || !form.vintage || !form.bottle || !form.closure || !form.filter) {
      alert("Please fill in all required fields.");
      return;
    }
    onAdd({ ...form });
    setForm(BLANK);
    onClose();
  }

  return (
    <Dialog open={open} onOpenChange={v => !v && onClose()}>
      <DialogContent className="max-w-2xl">
        <DialogHeader><DialogTitle>Add Wine / SKU</DialogTitle></DialogHeader>
        <div className="grid gap-4 py-4">
          <div className="grid grid-cols-2 gap-4">
            <FormField label="Brand Name *"><Input value={form.brand} onChange={e => set("brand", e.target.value)} placeholder="e.g. Hillcrest Estate" /></FormField>
            <FormField label="Wine Name *"><Input value={form.wine} onChange={e => set("wine", e.target.value)} placeholder="e.g. Pinot Noir Reserve" /></FormField>
          </div>
          <div className="grid grid-cols-3 gap-4">
            <FormField label="Vintage *"><Input value={form.vintage} onChange={e => set("vintage", e.target.value)} placeholder="2023 or NV" /></FormField>
            <FormField label="Bottle Type *">
              <Input list="modal-bottle-types" value={form.bottle} onChange={e => set("bottle", e.target.value)} placeholder="e.g. Burgundy" />
              <datalist id="modal-bottle-types">{BOTTLE_TYPES.map(b => <option key={b}>{b}</option>)}</datalist>
            </FormField>
            <FormField label="Format *">
              <Select value={form.format} onValueChange={v => set("format", v)}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>{BOTTLE_FORMATS.map(f => <SelectItem key={f} value={f}>{f}</SelectItem>)}</SelectContent>
              </Select>
            </FormField>
          </div>
          <div className="grid grid-cols-3 gap-4">
            <FormField label="Closure *">
              <Select value={form.closure || ""} onValueChange={v => set("closure", v)}>
                <SelectTrigger><SelectValue placeholder="Select…" /></SelectTrigger>
                <SelectContent><SelectItem value="Cork">Cork</SelectItem><SelectItem value="Screw Cap">Screw Cap</SelectItem></SelectContent>
              </Select>
            </FormField>
            <FormField label="Label">
              <Select value={form.label} onValueChange={v => set("label", v)}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent><SelectItem value="Yes">Yes</SelectItem><SelectItem value="No">No</SelectItem></SelectContent>
              </Select>
            </FormField>
            <FormField label="Capsule">
              <Select value={form.capsule} onValueChange={v => set("capsule", v)}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent><SelectItem value="No">No</SelectItem><SelectItem value="Yes">Yes</SelectItem></SelectContent>
              </Select>
            </FormField>
          </div>
          <div className="grid grid-cols-3 gap-4">
            <FormField label="Case Pack *"><Input type="number" value={form.casePack} onChange={e => set("casePack", Number(e.target.value))} min={1} /></FormField>
            <FormField label="Est. Cases *"><Input type="number" value={form.cases || ""} onChange={e => set("cases", Number(e.target.value))} placeholder="e.g. 100" /></FormField>
            <FormField label="Filter *">
              <Select value={form.filter || ""} onValueChange={v => set("filter", v)}>
                <SelectTrigger><SelectValue placeholder="Select…" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="10µ/BC">10µ / BC (Nominal)</SelectItem>
                  <SelectItem value="Sterile">Sterile (0.45µ)</SelectItem>
                </SelectContent>
              </Select>
            </FormField>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <FormField label="DMDC">
              <Select value={form.dmdc} onValueChange={v => set("dmdc", v)}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent><SelectItem value="No">No</SelectItem><SelectItem value="Yes">Yes</SelectItem></SelectContent>
              </Select>
            </FormField>
            <FormField label="Lot Code"><Input value={form.lotCode} onChange={e => set("lotCode", e.target.value)} placeholder="e.g. WTC252" /></FormField>
          </div>
          <FormField label="Fanciful Name / Vineyard"><Input value={form.fanciful} onChange={e => set("fanciful", e.target.value)} placeholder="e.g. Wild Thing, Garys' Vineyard" /></FormField>
          <FormField label="AVA / Appellation"><Input value={form.ava} onChange={e => set("ava", e.target.value)} placeholder="e.g. Sonoma Coast, RRV" /></FormField>
          <FormField label="Special Instructions"><Textarea value={form.instructions} onChange={e => set("instructions", e.target.value)} placeholder="Any notes for the bottling line…" /></FormField>
        </div>
        <DialogFooter>
          <Button variant="secondary" onClick={onClose}>Cancel</Button>
          <Button onClick={handleAdd}>Add Wine to Event</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ── Submit Modal ──────────────────────────────────────────────────────────

export function SubmitModal({
  open, onClose, onConfirm, dateFrom, dateTo, skuCount, totalCases,
}: {
  open: boolean; onClose: () => void; onConfirm: () => void;
  dateFrom: string; dateTo: string; skuCount: number; totalCases: number;
}) {
  const dateRange = [dateFrom, dateTo].filter(Boolean).map(d => fmtDate(d)).join(" – ");
  return (
    <Dialog open={open} onOpenChange={v => !v && onClose()}>
      <DialogContent>
        <DialogHeader><DialogTitle>Submit for Review</DialogTitle></DialogHeader>
        <div className="space-y-4 py-4">
          <div className="rounded-md bg-muted p-4 space-y-2 text-sm">
            <SummaryRow label="Preferred Window" value={dateRange || "—"} />
            <SummaryRow label="Total SKUs" value={skuCount} />
            <SummaryRow label="Total Cases" value={totalCases.toLocaleString()} />
          </div>
          <div className="flex gap-2 rounded-md border border-info bg-info/10 p-3 text-xs text-info-foreground">
            <span>Gravity Wine House will be notified by email when you submit.</span>
          </div>
          <p className="text-xs text-muted-foreground leading-relaxed">
            Once submitted, your bottling event will be locked for editing until Gravity Wine House has reviewed it. Changes made after confirmation — particularly inside 30 days — may incur fees.
          </p>
        </div>
        <DialogFooter>
          <Button variant="secondary" onClick={onClose}>Cancel</Button>
          <Button onClick={onConfirm}>Confirm & Submit</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ── Confirm Date Modal ────────────────────────────────────────────────────

export function ConfirmDateModal({ open, onClose, onConfirm }: { open: boolean; onClose: () => void; onConfirm: (date: string, lockDate: string | null) => void }) {
  const [date, setDate] = useState("");
  const lockDate = calcLockDate(date);
  return (
    <Dialog open={open} onOpenChange={v => !v && onClose()}>
      <DialogContent className="max-w-md">
        <DialogHeader><DialogTitle>Confirm Bottling Date</DialogTitle></DialogHeader>
        <div className="space-y-4 py-4">
          <FormField label="Confirmed Bottling Date *"><Input type="date" value={date} onChange={e => setDate(e.target.value)} /></FormField>
          <FormField label="Calculated Lock Date">
            <div className="h-9 rounded-md border border-border bg-muted px-3 py-2 font-mono text-sm text-muted-foreground">
              {lockDate ? `${fmtDate(lockDate)} (7 days prior)` : "—"}
            </div>
          </FormField>
          <div className="rounded-md border border-success/30 bg-success/10 p-3 text-xs text-success-foreground">
            Client will be notified by email with the confirmed date and lock date.
          </div>
        </div>
        <DialogFooter>
          <Button variant="secondary" onClick={onClose}>Cancel</Button>
          <Button onClick={() => onConfirm(date, lockDate)}>Confirm Date</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ── Assign Date Modal ─────────────────────────────────────────────────────

export function AssignDateModal({ open, onClose, onConfirm }: { open: boolean; onClose: () => void; onConfirm: (date: string, lockDate: string | null) => void }) {
  const [date, setDate] = useState("");
  const lockDate = calcLockDate(date);
  return (
    <Dialog open={open} onOpenChange={v => !v && onClose()}>
      <DialogContent className="max-w-sm">
        <DialogHeader><DialogTitle>Assign Bottling Date</DialogTitle></DialogHeader>
        <div className="space-y-4 py-4">
          <p className="text-sm text-muted-foreground leading-relaxed">Assign a confirmed bottling date to this unscheduled event.</p>
          <FormField label="Confirmed Date *"><Input type="date" value={date} onChange={e => setDate(e.target.value)} /></FormField>
          <FormField label="Calculated Lock Date">
            <div className="h-9 rounded-md border border-border bg-muted px-3 py-2 font-mono text-sm text-muted-foreground">{lockDate ? fmtDate(lockDate) : "—"}</div>
            <p className="text-[11px] text-muted-foreground mt-1">7 days before the bottling date.</p>
          </FormField>
        </div>
        <DialogFooter>
          <Button variant="secondary" onClick={onClose}>Cancel</Button>
          <Button onClick={() => onConfirm(date, lockDate)}>Confirm Date</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ── Unlock Modal ──────────────────────────────────────────────────────────

export function UnlockModal({ open, onClose, onConfirm }: { open: boolean; onClose: () => void; onConfirm: (note: string) => void }) {
  const [note, setNote] = useState("");
  return (
    <Dialog open={open} onOpenChange={v => !v && onClose()}>
      <DialogContent className="max-w-md">
        <DialogHeader><DialogTitle>Request Changes / Unlock</DialogTitle></DialogHeader>
        <div className="py-4">
          <FormField label="Change Request Note *">
            <Textarea value={note} onChange={e => setNote(e.target.value)} placeholder="Describe what needs to be updated…" className="min-h-[100px]" />
            <p className="text-[11px] text-muted-foreground mt-1">This will unlock the event back to DRAFT and notify the client.</p>
          </FormField>
        </div>
        <DialogFooter>
          <Button variant="secondary" onClick={onClose}>Cancel</Button>
          <Button onClick={() => { onConfirm(note); setNote(""); }}>Send & Unlock</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ── Edit Warning Modal ────────────────────────────────────────────────────

export function EditWarningModal({ open, onClose, onConfirm, confirmedDate }: { open: boolean; onClose: () => void; onConfirm: () => void; confirmedDate?: string | null }) {
  const daysOut = confirmedDate
    ? Math.ceil((new Date(confirmedDate + "T12:00:00").getTime() - Date.now()) / 86400000)
    : 999;
  const isLate = daysOut <= 30;
  return (
    <Dialog open={open} onOpenChange={v => !v && onClose()}>
      <DialogContent className="max-w-md">
        <DialogHeader><DialogTitle>{isLate ? "Edit Confirmed Event?" : "Edit This Event?"}</DialogTitle></DialogHeader>
        <div className="space-y-4 py-4">
          <p className="text-sm leading-relaxed">
            {isLate
              ? <>Editing will reset the status back to <strong>Submitted</strong>. GWH will need to re-review.</>
              : <>Your date is <strong>{daysOut} days away</strong> — free to make changes. Status resets to <strong>Submitted</strong>.</>}
          </p>
          {isLate ? (
            <div className="flex gap-2 rounded-md border border-warning/30 bg-warning/10 p-3 text-xs">
              You&apos;re within <strong>30 days</strong> of your bottling date. Late-change policies may apply.
            </div>
          ) : (
            <div className="flex gap-2 rounded-md border border-success/30 bg-success/10 p-3 text-xs">
              No late-change fees apply at this stage.
            </div>
          )}
        </div>
        <DialogFooter>
          <Button variant="secondary" onClick={onClose}>Cancel</Button>
          <Button onClick={onConfirm}>Yes, Edit Event</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ── Mark Complete Modal ───────────────────────────────────────────────────

export function MarkCompleteModal({ open, onClose, onConfirm }: { open: boolean; onClose: () => void; onConfirm: () => void }) {
  return (
    <Dialog open={open} onOpenChange={v => !v && onClose()}>
      <DialogContent className="max-w-sm">
        <DialogHeader><DialogTitle>Mark Event as Complete?</DialogTitle></DialogHeader>
        <div className="space-y-3 py-4 text-sm">
          <p>This marks the bottling event as <strong>Completed</strong> and moves it to the client&apos;s Past Events section.</p>
          <p className="text-muted-foreground">This action can be undone by contacting your database administrator.</p>
        </div>
        <DialogFooter>
          <Button variant="secondary" onClick={onClose}>Cancel</Button>
          <Button variant="default" onClick={onConfirm}>Mark Complete</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ── Export Modal ──────────────────────────────────────────────────────────

export function ExportModal({ open, onClose, onExport }: {
  open: boolean; onClose: () => void;
  onExport: (opts: { status: string; format: string; dateFrom: string; dateTo: string; inclEvents: boolean; inclSkus: boolean }) => void;
}) {
  const [status, setStatus] = useState("all");
  const [format, setFormat] = useState("csv");
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const [inclEvents, setInclEvents] = useState(false);
  const [inclSkus, setInclSkus] = useState(true);

  return (
    <Dialog open={open} onOpenChange={v => !v && onClose()}>
      <DialogContent className="max-w-lg">
        <DialogHeader><DialogTitle>Export Events</DialogTitle></DialogHeader>
        <div className="space-y-4 py-4">
          <div className="grid grid-cols-2 gap-4">
            <FormField label="Status">
              <Select value={status} onValueChange={setStatus}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  {["all", "submitted", "confirmed", "locked", "completed"].map(s => (
                    <SelectItem key={s} value={s}>{s.charAt(0).toUpperCase() + s.slice(1)}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </FormField>
            <FormField label="Format">
              <Select value={format} onValueChange={setFormat}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="csv">CSV</SelectItem>
                  <SelectItem value="xlsx">Excel (.xlsx)</SelectItem>
                </SelectContent>
              </Select>
            </FormField>
            <FormField label="Date From"><Input type="date" value={dateFrom} onChange={e => setDateFrom(e.target.value)} /></FormField>
            <FormField label="Date To"><Input type="date" value={dateTo} onChange={e => setDateTo(e.target.value)} /></FormField>
          </div>
          <FormField label="Include">
            <div className="flex flex-col gap-2 mt-1">
              <CheckRow checked={inclEvents} onChange={setInclEvents} label="Events summary (one row per event)" />
              <CheckRow checked={inclSkus} onChange={setInclSkus} label="SKUs with event info (one row per wine — recommended)" />
            </div>
          </FormField>
        </div>
        <DialogFooter>
          <Button variant="secondary" onClick={onClose}>Cancel</Button>
          <Button onClick={() => onExport({ status, format, dateFrom, dateTo, inclEvents, inclSkus })}>Download</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ── Daily Sheet Modal ─────────────────────────────────────────────────────

export function DailySheetModal({ open, onClose, onPrint, children }: { open: boolean; onClose: () => void; onPrint: () => void; children: React.ReactNode }) {
  return (
    <Dialog open={open} onOpenChange={v => !v && onClose()}>
      <DialogContent className="max-w-3xl">
        <DialogHeader><DialogTitle>Daily Bottling Sheet</DialogTitle></DialogHeader>
        <div id="daily-sheet-modal-body" className="px-7 py-5 max-h-[60vh] overflow-y-auto">{children}</div>
        <DialogFooter>
          <Button variant="secondary" onClick={onClose}>Close</Button>
          <Button onClick={onPrint}>Print / Download PDF</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
